import { yupResolver } from '@hookform/resolvers/yup';
import clsx from 'clsx';
import { createContext, useCallback, useMemo, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useForm } from 'react-hook-form';

import eyeOffIcon from '../../../assets/images/icons/eye-off.svg';
import eyeIcon from '../../../assets/images/icons/eye.svg';
import lockGlowIcon from '../../../assets/images/icons/lock-glow.svg';
import Input from './Input';
import CustomModal from './CustomModal';
import { FIELD_TEXT_MESSAGES } from '../../../config';
import { PasswordSchema } from '../../auth/schemas/index';
import { useAppSelector } from '../../../store';
import alert from '../../../utils/alert';
import { AuthError, getCurrentUser, updatePassword } from 'aws-amplify/auth';
import { useTranslation } from 'react-i18next';

type CallbackFunction = (...args: any[]) => void;
export type WithSilentAuthFN = (callback: CallbackFunction) => void;

export function createSilentAuthContext() {
  return createContext<WithSilentAuthFN | null>(null);
}

type Props = {
  context?: ReturnType<typeof createSilentAuthContext>;
  disableSingleSignIn?: boolean;
  renderItem: (withSilentAuth: WithSilentAuthFN) => React.ReactNode;
};

export default function SilentAuthProvider({ renderItem, context, disableSingleSignIn }: Props) {
  const { username } = useAppSelector((state: any) => state.auth);
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    criteriaMode: 'all',
    resolver: yupResolver(PasswordSchema),
  });

  const [hasAuthSilently, setHasAuthSilently] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [inputFieldType, setInputFieldType] = useState('password');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const callbackRef = useRef<CallbackFunction>();

  const togglePasswordVisibility = (e: React.SyntheticEvent<EventTarget>) => {
    e.preventDefault();
    setInputFieldType(inputFieldType === 'text' ? 'password' : 'text');
  };

  const withSilentAuth = useCallback(
    (callback: CallbackFunction) => {
      if (!disableSingleSignIn && hasAuthSilently) {
        callback();
        return;
      }
      callbackRef.current = callback;
      setShowModal(true);
    },
    [disableSingleSignIn, hasAuthSilently],
  );

  const onSuccessfulAuth = useCallback(() => {
    setHasAuthSilently(true);
    setShowModal(false);
    callbackRef.current?.();
    callbackRef.current = undefined;
    reset();
  }, [reset]);
  const onSubmitForm = useCallback(
    async (data: any) => {
      try {
        setIsSubmitting(true);
        const { password } = data;
        if (!password) return;

        try {
          const currentUser = await getCurrentUser();

          if (currentUser.username !== username) {
            throw new AuthError({
              name: 'NotAuthorizedException',
              message: 'User is not authenticated or username mismatch',
            });
          }

          await updatePassword({ oldPassword: password, newPassword: password });

          alert.success(t('Password verified successfully.'));
          onSuccessfulAuth();
        } catch (error: any) {
          if (error instanceof AuthError) {
            if (
              error.name === 'NotAuthorizedException' ||
              error.name === 'InvalidPasswordException'
            ) {
              alert.error(t('Incorrect password. Please try again.'), {
                toastId: 'silent-auth-error',
              });
            } else if (error.name === 'LimitExceededException') {
              alert.error(t('Too many attempts. Please try again later.'), {
                toastId: 'silent-auth-error',
              });
            } else {
              alert.error(t('Unable to verify user information. Please try again later.'), {
                toastId: 'silent-auth-error',
              });
            }
          } else {
            throw error;
          }
        }
      } catch (error: any) {
        let message = FIELD_TEXT_MESSAGES.GENERAL_ERROR;
        if (error instanceof AuthError) {
          message = error.message;
        }
        alert.error(message, { toastId: 'silent-auth-error' });
      } finally {
        setIsSubmitting(false);
      }
    },
    [onSuccessfulAuth, username, t],
  );
  const content = useMemo(
    () =>
      context ? (
        <context.Provider value={withSilentAuth}>{renderItem(withSilentAuth)}</context.Provider>
      ) : (
        renderItem(withSilentAuth)
      ),
    [context, renderItem, withSilentAuth],
  );

  return (
    <>
      {content}
      <CustomModal
        className='account__modal account__modal--password'
        title={t('Enter your password')}
        helpText={t('For security reasons please enter your password.')}
        show={showModal}
        showCustomHeaderIcon
        customHeaderIcon={<img src={lockGlowIcon} alt='' />}
        isSubmitting={isSubmitting}
        confirmText='Authenticate'
        handleCancel={() => setShowModal(false)}
        handleConfirm={(e) => {
          e?.preventDefault();
          e?.stopPropagation();
          handleSubmit(onSubmitForm)(e);
        }}
      >
        <form
          onSubmit={(e) => {
            e?.preventDefault();
            e?.stopPropagation();
            handleSubmit(onSubmitForm)(e);
          }}
        >
          <Form.Group
            controlId='password'
            className='auth__input-group auth__input-group--relative'
          >
            <Input
              type={inputFieldType}
              autoComplete='off'
              placeholder={t('Password')}
              className={clsx('auth__input', 'auth__input-no-bootstrap-icon', [
                errors.password && 'is-invalid',
              ])}
              data-testid={'password-input'}
              {...register('password')}
            />
            <button
              className='no-style-btn auth__input-group-icon-no-label auth__input-group-icon'
              onClick={togglePasswordVisibility}
              type={'button'}
            >
              {inputFieldType === 'password' ? (
                <img src={eyeOffIcon} width={20} height={20} alt='eye-off-icon' />
              ) : (
                <img src={eyeIcon} width={20} height={20} alt='eye-icon' />
              )}
            </button>
          </Form.Group>
        </form>
      </CustomModal>
    </>
  );
}
